// Types for the 0x21 dimming profile, mirroring the Go backend
// `DimmingProfileEntity` (entities/dimming_profile.entity.go) and the device
// 0x21 wire contract (decoder_ul20xx_1_1_x.js, plan §3). Field names MUST match
// the Go JSON tags byte-for-byte so the same JSON round-trips cloud <-> front.

// A single step of the dimming curve. `step_time_minutes` MUST be on a 10-minute
// boundary (0, 10, 20, ... 1430). The device stores `minutes/10`, so non-aligned
// values are rejected/rounded by the editor and the encoder.
export interface ProfileStep {
  step_time_minutes: number // 0..1430 (00:00..23:50), multiple of 10
  dimming_level: number // 0..100 (%), or 0xFF (255) = inactive
}

// Full profile shape stored on the platform. `active_days` is a uint8 bitfield:
// bit0=holiday, bit1=mon, bit2=tue, ... bit7=sun (LSB-first, plan §3).
// `dali_address` is a uint8: 0x01 analog, 0xFE DALI broadcast, 0xFF all devices
// (default). Group iff (addr & 0x80); single iff !(addr & 0x80) && !(addr & 0x01).
export interface DimmingProfile {
  profile_id: number // 0..254; 255 reserved for "no_profile"
  fleet_id: string
  version: number
  steps: ProfileStep[]
  active_days: number // uint8 bitfield
  dali_address: number // uint8, default 0xFF
  // 0x20 zenith sunrise/sunset calendar. Optional: the profile document now
  // carries BOTH the 0x21 steps and the calendar; older docs have no calendar.
  calendar?: Calendar
}

// ── 0x20 zenith calendar (sunrise/sunset) ───────────────────────────────────
// Mirrors the Go `Calendar` struct embedded in DimmingProfileEntity. A step maps
// a solar zenith angle (deg) to a dimming level (%). Zenith 90° = horizon; >90°
// = sun below the horizon (twilight). Saved/loaded with the 0x21 profile through
// the SAME /dimming-profiles/{fleet_id} endpoint.
export interface CalendarStep {
  zenith_angle_deg: number // e.g. 90 = horizon, 100/105 = below horizon
  dimming_level_percent: number // 0..100 (%)
}

export interface Calendar {
  sunrise_steps: CalendarStep[]
  sunset_steps: CalendarStep[]
  latitude_deg: number
  longitude_deg: number
  prefers_meta_pos: boolean
  clamps_profiles: boolean // default true: the calendar clamps the 0x21 profile
  clamps_dig: boolean
  ignore_gnss: boolean
}

// The editable subset the SVG editor produces/consumes. profile_id/fleet_id/
// version are owned by the backend; the editor only edits steps + days + address.
export type DimmingProfileDraft = Pick<
  DimmingProfile,
  'steps' | 'active_days' | 'dali_address'
>

// ── active_days bit constants (plan §3, LSB-first) ──────────────────────────
export const DAY_BITS = {
  holiday: 1 << 0,
  mon: 1 << 1,
  tue: 1 << 2,
  wed: 1 << 3,
  thu: 1 << 4,
  fri: 1 << 5,
  sat: 1 << 6,
  sun: 1 << 7,
} as const

export type DayKey = keyof typeof DAY_BITS

// Display/iteration order (holiday last so the week reads Mon..Sun first).
export const DAY_ORDER: DayKey[] = [
  'mon',
  'tue',
  'wed',
  'thu',
  'fri',
  'sat',
  'sun',
  'holiday',
]

// ── dali_address constants (plan §3, addressParse) ──────────────────────────
export const DALI_ANALOG = 0x01
export const DALI_BROADCAST = 0xfe
export const DALI_ALL_DEVICES = 0xff // default

// The kind of DALI addressing the operator selected. `single` and `group`
// carry an extra id encoded into the address byte.
export type DaliAddressMode = 'analog' | 'broadcast' | 'all' | 'single' | 'group'

// ── time / level constants ──────────────────────────────────────────────────
export const TIME_STEP_MINUTES = 10 // 10-minute grid (plan §3)
export const MIN_TIME_MINUTES = 0 // 00:00
export const MAX_TIME_MINUTES = 1430 // 23:50
export const MIN_LEVEL = 0
export const MAX_LEVEL = 100
export const LEVEL_INACTIVE = 0xff // 255 = inactive (distinct visual)

// ── calendar (0x20) defaults ────────────────────────────────────────────────
// A fresh editor starts empty (no steps, lat/long 0) with clamps_profiles=true
// (the calendar borne le profil horaire by default) and the other flags off.
export const emptyCalendar = (): Calendar => ({
  sunrise_steps: [],
  sunset_steps: [],
  latitude_deg: 0,
  longitude_deg: 0,
  prefers_meta_pos: false,
  clamps_profiles: true,
  clamps_dig: false,
  ignore_gnss: false,
})

// True iff the calendar carries no operator-entered data, so the editor can omit
// it from the save payload entirely (the fleet then keeps "no calendar").
// Compromis coordonnées (revue MR !2) : faute de sentinelle null dans le modèle,
// on ne traite les coordonnées comme « non renseignées » que lorsque lat ET lng
// valent 0 simultanément. Une latitude 0 seule (équateur) avec une longitude non
// nulle est une position valide et rend donc le calendrier NON vide.
export const isCalendarEmpty = (c: Calendar): boolean =>
  c.sunrise_steps.length === 0 &&
  c.sunset_steps.length === 0 &&
  c.latitude_deg === 0 &&
  c.longitude_deg === 0
